import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, KeyRound, Trash2, Unlock } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Role } from "@/features/auth/types";
import { useAuthStore } from "@/features/auth/store";
import {
  ROLE_OPTIONS,
  dateText,
  errText,
  fullName,
  platformApi,
  type AdminUserDetail,
  type UserUpdate,
} from "@/admin/api";
import { NativeSelect, TenantPicker } from "@/admin/components";
import { UserFlags } from "@/admin/pages/AdminUsersPage";

export default function AdminUserDetailPage() {
  const { id = "" } = useParams();
  const q = useQuery({ queryKey: ["admin", "user", id], queryFn: () => platformApi.user(id) });
  if (q.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (!q.data) return <p className="text-sm text-muted-foreground">User not found.</p>;
  return <UserDetail user={q.data} />;
}

function useUserMutation<A>(id: string, fn: (a: A) => Promise<AdminUserDetail>, ok: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (u) => {
      qc.setQueryData(["admin", "user", id], u);
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "tenant"] });
      toast.success(ok);
    },
    onError: (e) => toast.error(errText(e)),
  });
}

function UserDetail({ user }: { user: AdminUserDetail }) {
  const [pwOpen, setPwOpen] = useState(false);
  const unlock = useUserMutation(user.id, () => platformApi.unlock(user.id), "Account unlocked.");

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/users" className="inline-flex items-center text-sm text-muted-foreground hover:underline">
          <ArrowLeft className="mr-1 h-4 w-4" /> Users
        </Link>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">{fullName(user)}</h1>
            <UserFlags u={user} />
          </div>
          <div className="flex gap-2">
            {(user.locked || user.failed_login_attempts > 0) && (
              <Button variant="outline" onClick={() => unlock.mutate(undefined)} disabled={unlock.isPending}>
                <Unlock className="mr-2 h-4 w-4" /> Unlock
              </Button>
            )}
            <Button variant="outline" onClick={() => setPwOpen(true)}>
              <KeyRound className="mr-2 h-4 w-4" /> Set password
            </Button>
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {user.email} · joined {dateText(user.created_at)} · last login {dateText(user.last_login_at)}
          {user.mfa_enabled && " · 2FA on"}
          {user.failed_login_attempts > 0 && ` · ${user.failed_login_attempts} failed logins`}
          {user.locked && ` · locked until ${dateText(user.locked_until)}`}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ProfileCard user={user} />
        <AccessCard user={user} />
      </div>
      <MembershipsCard user={user} />

      {pwOpen && <PasswordDialog user={user} onClose={() => setPwOpen(false)} />}
    </div>
  );
}

function ProfileCard({ user }: { user: AdminUserDetail }) {
  const initial = {
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    role: user.role,
    tenant: user.tenant,
  };
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [JSON.stringify(initial)]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty =
    form.first_name !== initial.first_name ||
    form.last_name !== initial.last_name ||
    form.email !== initial.email ||
    form.role !== initial.role ||
    form.tenant?.id !== initial.tenant?.id;

  const save = useUserMutation(
    user.id,
    () => {
      const body: UserUpdate = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        role: form.role,
      };
      if (form.tenant?.id !== initial.tenant?.id) body.tenant_id = form.tenant?.id ?? null;
      return platformApi.updateUser(user.id, body);
    },
    "Saved.",
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Profile</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>First name</Label>
            <Input value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Last name</Label>
            <Input value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
        </div>
        <div className="space-y-1.5">
          <Label>Home tenant</Label>
          <TenantPicker value={form.tenant} onChange={(t) => setForm((f) => ({ ...f, tenant: t }))} allowNone />
          <p className="text-xs text-muted-foreground">The workspace they land in after login.</p>
        </div>
        <div className="space-y-1.5">
          <Label>Role in home tenant</Label>
          <NativeSelect value={form.role} onChange={(v) => setForm((f) => ({ ...f, role: v as Role }))}>
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="flex justify-end">
          <Button disabled={!dirty || save.isPending} onClick={() => save.mutate(undefined)}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AccessCard({ user }: { user: AdminUserDetail }) {
  const myId = useAuthStore((s) => s.user?.id);
  const myself = myId === user.id;
  const update = useUserMutation(user.id, (d: UserUpdate) => platformApi.updateUser(user.id, d), "Saved.");

  const rows: { key: "is_active" | "email_verified" | "is_superuser"; label: string; help: string; value: boolean }[] = [
    { key: "is_active", label: "Account enabled", help: "Off blocks this login everywhere.", value: user.is_active },
    { key: "email_verified", label: "Email verified", help: "Unverified users are asked to confirm their email.", value: user.email_verified },
    { key: "is_superuser", label: "Superuser", help: "Full access to every tenant and to this admin.", value: user.is_superuser },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Access</CardTitle>
      </CardHeader>
      <CardContent className="divide-y">
        {rows.map((r) => {
          const locked = myself && r.key !== "email_verified";
          return (
            <div key={r.key} className="flex items-center justify-between gap-4 py-3">
              <div>
                <div className="text-sm font-medium">{r.label}</div>
                <div className="text-xs text-muted-foreground">
                  {locked ? "You can't change this on your own account." : r.help}
                </div>
              </div>
              <Switch
                checked={r.value}
                disabled={locked || update.isPending}
                onCheckedChange={(v) => update.mutate({ [r.key]: v })}
              />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function MembershipsCard({ user }: { user: AdminUserDetail }) {
  const [tenant, setTenant] = useState<{ id: string; name: string } | null>(null);
  const [role, setRole] = useState<Role>("member");
  const add = useUserMutation(
    user.id,
    () => platformApi.addMembership(user.id, tenant!.id, role),
    "Added to tenant.",
  );
  const change = useUserMutation(
    user.id,
    ({ id, ...d }: { id: string; role?: Role; is_active?: boolean }) => platformApi.updateMembership(id, d),
    "Saved.",
  );
  const remove = useUserMutation(user.id, (id: string) => platformApi.removeMembership(id), "Removed from tenant.");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Workspaces</CardTitle>
        <p className="text-sm text-muted-foreground">
          Every tenant this login can switch into, and their role in each.
        </p>
      </CardHeader>
      <CardContent className="space-y-4 p-0">
        {user.memberships.length === 0 ? (
          <p className="px-6 text-sm text-muted-foreground">Not a member of any tenant.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-6 py-2 font-medium">Tenant</th>
                <th className="px-4 py-2 font-medium">Role</th>
                <th className="px-4 py-2 font-medium">Active</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {user.memberships.map((m) => (
                <tr key={m.id} className="border-b last:border-0">
                  <td className="px-6 py-2">
                    <Link to={`/admin/tenants/${m.tenant.id}`} className="font-medium hover:underline">
                      {m.tenant.name}
                    </Link>
                    {m.is_home && <span className="ml-2 text-xs text-muted-foreground">(home)</span>}
                  </td>
                  <td className="px-4 py-2">
                    <NativeSelect
                      value={m.role}
                      onChange={(v) => change.mutate({ id: m.id, role: v as Role })}
                      className="w-36"
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </NativeSelect>
                  </td>
                  <td className="px-4 py-2">
                    <Switch
                      checked={m.is_active}
                      onCheckedChange={(v) => change.mutate({ id: m.id, is_active: v })}
                    />
                  </td>
                  <td className="px-4 py-2 text-right">
                    {!m.is_home && (
                      <Button variant="ghost" size="sm" onClick={() => remove.mutate(m.id)} title="Remove from tenant">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="flex flex-wrap items-end gap-3 border-t px-6 py-4">
          <div className="min-w-[260px] flex-1 space-y-1.5">
            <Label>Add to tenant</Label>
            <TenantPicker value={tenant} onChange={setTenant} />
          </div>
          <div className="w-36 space-y-1.5">
            <Label>Role</Label>
            <NativeSelect value={role} onChange={(v) => setRole(v as Role)}>
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </NativeSelect>
          </div>
          <Button
            disabled={!tenant || add.isPending}
            onClick={() =>
              add.mutate(undefined, {
                onSuccess: () => setTenant(null),
              })
            }
          >
            Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PasswordDialog({ user, onClose }: { user: AdminUserDetail; onClose: () => void }) {
  const [password, setPassword] = useState("");
  const save = useUserMutation(user.id, () => platformApi.setPassword(user.id, password), "Password changed.");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set password</DialogTitle>
          <DialogDescription>
            New password for {user.email}. This also clears any login lock.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(undefined, { onSuccess: onClose });
          }}
        >
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            autoFocus
            required
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Set password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
